<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import InboxesAPI from 'dashboard/api/inboxes';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
  active: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();

const isLoading = ref(false);
const hasLoaded = ref(false);
const mediaItems = ref([]);
const searchQuery = ref('');
const deletingMediaId = ref('');

const filteredMediaItems = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();
  if (!query) return mediaItems.value;

  return mediaItems.value.filter(item =>
    [item.media_id, item.filename, item.mime_type, item.file_type]
      .filter(Boolean)
      .some(value => value.toString().toLowerCase().includes(query))
  );
});

const loadMedia = async () => {
  isLoading.value = true;
  try {
    const response = await InboxesAPI.getWhatsAppMedia(props.inbox.id);
    mediaItems.value = response.data?.payload || [];
    hasLoaded.value = true;
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_MEDIA_LOAD_ERROR')
    );
  } finally {
    isLoading.value = false;
  }
};

const deleteMedia = async mediaId => {
  deletingMediaId.value = mediaId;
  try {
    const response = await InboxesAPI.deleteWhatsAppMedia(props.inbox.id, mediaId);
    mediaItems.value = response.data?.payload || [];
    useAlert(t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_MEDIA_DELETE_SUCCESS'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_MEDIA_DELETE_ERROR')
    );
  } finally {
    deletingMediaId.value = '';
  }
};

watch(
  () => props.active,
  isActive => {
    if (isActive && !hasLoaded.value) loadMedia();
  },
  { immediate: true }
);
</script>

<template>
  <div class="grid gap-4">
    <div class="flex flex-wrap items-center justify-between gap-3">
      <p class="mb-0 text-sm text-n-slate-11">
        {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_MEDIA_NOTE') }}
      </p>
      <Button
        type="button"
        variant="faded"
        color="slate"
        icon="i-ph-arrows-clockwise"
        :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_MEDIA_REFRESH')"
        :is-loading="isLoading"
        @click="loadMedia"
      />
    </div>

    <label class="grid gap-1 text-sm text-n-slate-12">
      <span class="font-medium">
        {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_MEDIA_SEARCH') }}
      </span>
      <input
        v-model="searchQuery"
        type="text"
        class="w-full rounded-lg border border-n-container bg-n-background px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand"
        :placeholder="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_MEDIA_SEARCH_PLACEHOLDER')"
      />
    </label>

    <div
      v-if="isLoading"
      class="rounded-xl border border-n-container bg-n-alpha-2 px-4 py-10 text-center text-sm text-n-slate-11"
    >
      {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_MEDIA_LOADING') }}
    </div>

    <div
      v-else-if="!mediaItems.length"
      class="rounded-xl border border-dashed border-n-container bg-n-alpha-2 px-4 py-10 text-center text-sm text-n-slate-11"
    >
      {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_MEDIA_EMPTY') }}
    </div>

    <div
      v-else-if="!filteredMediaItems.length"
      class="rounded-xl border border-dashed border-n-container bg-n-alpha-2 px-4 py-10 text-center text-sm text-n-slate-11"
    >
      {{ $t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_MEDIA_FILTER_EMPTY') }}
    </div>

    <div v-else class="grid gap-3">
      <div
        v-for="item in filteredMediaItems"
        :key="item.media_id"
        class="rounded-xl border border-n-container bg-n-alpha-2 p-4"
      >
        <div class="flex flex-wrap items-start justify-between gap-3">
          <div class="min-w-0">
            <p class="mb-1 truncate text-sm font-semibold text-n-slate-12">
              {{ item.filename || item.media_id }}
            </p>
            <p class="mb-1 break-all text-xs text-n-slate-11">
              {{ item.media_id }}
            </p>
            <p class="mb-0 text-xs text-n-slate-11">
              {{ item.file_type }} · {{ item.mime_type }} · {{ item.uploaded_at }}
            </p>
          </div>
          <Button
            type="button"
            variant="faded"
            color="slate"
            icon="i-lucide-trash-2"
            :label="$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_MEDIA_DELETE')"
            :is-loading="deletingMediaId === item.media_id"
            @click="deleteMedia(item.media_id)"
          />
        </div>
      </div>
    </div>
  </div>
</template>
